<template>
  <div v-loading="loading" class="analytics-container">
    <div class="analytics-content">
      <!-- Error Banner if Credentials or API fail -->
      <el-alert
        v-if="analyticsData.errorMsg"
        :title="analyticsData.errorMsg"
        type="warning"
        show-icon
        closable
        class="mb-4"
      ></el-alert>

      <!-- 1. Header Control -->
      <HeaderControl
        :current-range="rangeType"
        :range-label="currentRangeLabel"
        @select-range="handleSelectRange"
      />

      <!-- 2. KPI Cards -->
      <KpiCards
        :kpis="analyticsData.kpis"
        :range-label="currentRangeLabel"
      />

      <!-- 3. Traffic & User Growth Charts -->
      <TrafficUserCharts
        :traffic-trend="analyticsData.charts.trafficTrend"
        :user-growth="analyticsData.charts.userGrowth"
        :range-label="currentRangeLabel"
      />

      <!-- 4. Content Growth & Country Share Charts -->
      <ContentCountryCharts
        :content-growth="analyticsData.charts.contentGrowth"
        :country-share="analyticsData.charts.countryShare"
        :range-label="currentRangeLabel"
        :content-loading="contentLoading"
      />

      <!-- 5. Detailed Access Table -->
      <PageStatsTable
        :page-stats="analyticsData.charts.pageStats"
        :qr-stats="analyticsData.charts.qrStats"
        :range-label="currentRangeLabel"
      />
    </div>

    <!-- Custom Date Range Dialog -->
    <el-dialog
      title="Chọn khoảng thời gian tùy chỉnh"
      :visible.sync="customDialogVisible"
      width="450px"
      append-to-body
      custom-class="custom-date-dialog"
    >
      <div class="dialog-body">
        <label class="block-label">Thời gian:</label>
        <el-date-picker
          v-model="customDateRange"
          type="daterange"
          range-separator="Đến"
          start-placeholder="Ngày bắt đầu"
          end-placeholder="Ngày kết thúc"
          format="dd/MM/yyyy"
          value-format="yyyy-MM-dd"
          style="width: 100%"
        ></el-date-picker>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="customDialogVisible = false">Hủy</el-button>
        <el-button type="primary" @click="applyCustomDate">Áp dụng</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'
import { getAnalytics } from '@/services/analytic'
import { formatRealtimeData, formatStandardData } from '@/services/gg-analytics/formatters'
import { getContentGrowthStats } from '@/services/gg-analytics/contentStats'
import HeaderControl, { RANGE_LABEL_MAP } from './components/HeaderControl.vue'
import KpiCards from './components/KpiCards.vue'
import TrafficUserCharts from './components/TrafficUserCharts.vue'
import ContentCountryCharts from './components/ContentCountryCharts.vue'
import PageStatsTable from './components/PageStatsTable.vue'

export default {
  name: 'GGAnalytics',
  components: {
    HeaderControl,
    KpiCards,
    TrafficUserCharts,
    ContentCountryCharts,
    PageStatsTable
  },
  data() {
    return {
      loading: false,
      contentLoading: false,
      contentGrowthData: [],
      rangeType: 'realtime',
      customStartDate: '',
      customEndDate: '',
      customDialogVisible: false,
      customDateRange: [],
      timer: null,
      analyticsData: {
        errorMsg: null,
        rangeType: 'realtime',
        rangeLabel: '30 phút qua',
        kpis: {
          totalVisits: 0,
          activeToday: 0,
          activeThisMonth: 0,
          newVisitors: 0,
          returningVisitors: 0,
          avgTimeSeconds: 0,
          totalPageViews: 0,
          bounceRate: 0,
          totalSearches: 0,
          totalQrScans: 0
        },
        charts: {
          trafficTrend: [],
          userGrowth: [],
          contentGrowth: [],
          countryShare: [],
          pageStats: [],
          qrStats: []
        }
      }
    }
  },
  computed: {
    ...mapGetters(['language']),
    currentRangeLabel() {
      if (this.rangeType === 'custom') {
        if (this.customStartDate && this.customEndDate) {
          return `${this.formatDisplayDate(this.customStartDate)} - ${this.formatDisplayDate(this.customEndDate)}`
        }
        return 'Tùy chỉnh'
      }
      return (
        RANGE_LABEL_MAP[this.rangeType] ||
        this.analyticsData.rangeLabel ||
        '30 phút qua'
      )
    }
  },
  watch: {
    language() {
      this.fetchAnalytics()
      this.fetchContentGrowth()
    }
  },
  created() {
    this.fetchAnalytics()
    this.fetchContentGrowth()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  methods: {
    formatDisplayDate(dStr) {
      if (!dStr) return ''
      try {
        return dayjs(dStr).format('DD/MM/YYYY')
      } catch (e) {
        return dStr
      }
    },

    async fetchContentGrowth() {
      this.contentLoading = true
      try {
        const data = await getContentGrowthStats(this.language)
        this.contentGrowthData = data || []
        if (this.analyticsData && this.analyticsData.charts) {
          this.$set(this.analyticsData.charts, 'contentGrowth', this.contentGrowthData)
        }
      } catch (e) {
        console.error('Failed to load Content Growth Stats:', e)
      } finally {
        this.contentLoading = false
      }
    },

    async fetchAnalytics() {
      this.loading = true
      try {
        const params = { range_type: this.rangeType }
        if (this.rangeType === 'custom' && this.customStartDate && this.customEndDate) {
          params.start_date = this.customStartDate
          params.end_date = this.customEndDate
        }

        const res = await getAnalytics(params)
        const rawData = res && res.data ? res.data : res

        if (rawData) {
          if (rawData.charts && rawData.kpis && typeof rawData.kpis.totalVisits === 'number') {
            this.analyticsData = {
              ...rawData,
              rangeType: this.rangeType,
              rangeLabel: this.currentRangeLabel,
              charts: {
                ...rawData.charts,
                contentGrowth:
                  rawData.charts.contentGrowth && rawData.charts.contentGrowth.length > 0
                    ? rawData.charts.contentGrowth
                    : this.contentGrowthData
              }
            }
          } else {
            if (this.rangeType === 'realtime') {
              this.analyticsData = formatRealtimeData(
                rawData.kpis,
                rawData.custom_events,
                rawData.trend,
                rawData.country,
                rawData.pages,
                this.contentGrowthData
              )
              this.analyticsData.rangeLabel = this.currentRangeLabel
            } else {
              this.analyticsData = formatStandardData(
                rawData.kpis,
                rawData.custom_events,
                rawData.trend,
                rawData.country,
                rawData.pages,
                rawData.qr_scans,
                this.rangeType,
                this.currentRangeLabel,
                this.contentGrowthData
              )
            }
          }
        }
      } catch (err) {
        console.error('Failed to load Analytics Data:', err)
        this.analyticsData.errorMsg =
          (err && err.message) || 'Có lỗi xảy ra khi tải dữ liệu thống kê từ máy chủ.'
      } finally {
        this.loading = false
      }
    },

    handleSelectRange(range) {
      if (range === 'custom') {
        this.customDialogVisible = true
        return
      }
      this.rangeType = range
      this.fetchAnalytics()

      if (range === 'realtime') {
        this.startAutoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    },

    applyCustomDate() {
      if (!this.customDateRange || this.customDateRange.length < 2) {
        this.$message.warning('Vui lòng chọn khoảng thời gian hợp lệ')
        return
      }
      this.customStartDate = this.customDateRange[0]
      this.customEndDate = this.customDateRange[1]
      this.rangeType = 'custom'
      this.customDialogVisible = false
      this.stopAutoRefresh()
      this.fetchAnalytics()
    },

    startAutoRefresh() {
      this.stopAutoRefresh()
      if (this.rangeType === 'realtime') {
        this.timer = setInterval(() => {
          this.fetchAnalytics()
        }, 30 * 60 * 1000)
      }
    },

    stopAutoRefresh() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.analytics-container {
  min-height: 100vh;
  background-color: #fbf9f5;
  padding: 24px;
  box-sizing: border-box;

  .analytics-content {
    max-width: 1400px;
    margin: 0 auto;
  }

  .mb-4 {
    margin-bottom: 16px;
  }
}
</style>

<style lang="scss">
.custom-date-dialog {
  border-radius: 16px !important;

  .el-dialog__header {
    font-weight: 700;
    padding: 20px 24px 10px;
  }

  .el-dialog__title {
    font-weight: 700;
    color: #111827;
  }

  .dialog-body {
    padding: 10px 0;

    .block-label {
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      margin-bottom: 8px;
      display: block;
    }
  }

  .el-dialog__footer {
    padding: 16px 24px 24px;
  }
}
</style>
